/**
 * Singleton WebSocket manager that lives outside React's effect lifecycle.
 * This ensures only one WebSocket connection exists regardless of component re-renders.
 */

import { getSupabase } from "@/lib/supabase";
import {
  WS_HEARTBEAT_INTERVAL_MS,
  WS_HEARTBEAT_TIMEOUT_MS,
  WS_RECONNECT_BASE_DELAY_MS,
  WS_RECONNECT_MAX_DELAY_MS,
  WS_CLOSE_HEARTBEAT_TIMEOUT,
} from "@/lib/constants";
import posthog from "posthog-js";
import { notifyAuthSessionExpired } from "@/lib/authSessionExpired";

// Import shared types (used by both server and client)
export type {
  RealtimeEvent,
  RealtimeMessage,
  RealtimeConversation,
  RealtimeCalendarEvent,
  MessageAttachment,
  AdReferral,
} from "@/types/realtime";
import type { RealtimeEvent } from "@/types/realtime";

export type RealtimeCallback = (event: RealtimeEvent) => void;

class RealtimeSocketManager {
  private ws: WebSocket | null = null;
  private connecting = false;
  private authenticated = false; // First-message auth state
  private reconnectAttempts = 0;
  private reconnectTimeout: number | undefined;
  private currentUserId: string | null = null;
  private isPageVisible = true;
  private visibilityHandler: (() => void) | null = null;
  private pendingToken: string | null = null; // Token to send on open
  private authRefreshAttemptedForUser: string | null = null;
  private authReconnectSuspended = false;
  private suppressReconnectForWs = new WeakSet<WebSocket>();

  // Heartbeat state
  private heartbeatInterval: number | undefined;
  private heartbeatTimeout: number | undefined;
  private lastPongAt: number = 0;
  private lastRtt: number = 0; // Round-trip time in ms

  // Callback registries
  private inboxCallbacks = new Set<RealtimeCallback>();
  private calendarCallbacks = new Set<RealtimeCallback>();
  private connectionChangeCallbacks = new Set<(connected: boolean) => void>();

  constructor() {
    // Set up page visibility handling
    this.setupVisibilityHandler();
  }

  private setupVisibilityHandler(): void {
    if (typeof document === "undefined") return; // SSR guard

    this.visibilityHandler = () => {
      const wasVisible = this.isPageVisible;
      this.isPageVisible = document.visibilityState === "visible";

      if (this.isPageVisible && !wasVisible) {
        // Page became visible - reconnect if we have a user and are disconnected
        console.log("[RealtimeSocket] Page visible - checking connection");
        if (this.currentUserId && !this.authReconnectSuspended && !this.connected && !this.connecting) {
          console.log("[RealtimeSocket] Reconnecting after visibility change");
          this.reconnectAttempts = 0; // Reset attempts on visibility change
          this.connect(this.currentUserId);
        }
        // Resume heartbeat if connected
        if (this.connected) {
          this.startHeartbeat();
        }
      } else if (!this.isPageVisible && wasVisible) {
        // Page became hidden - pause heartbeat to save battery/resources
        // Server has its own idle timeout, connection will be maintained
        console.log("[RealtimeSocket] Page hidden - pausing heartbeat");
        this.stopHeartbeat();
      }
    };

    document.addEventListener("visibilitychange", this.visibilityHandler);
  }

  /**
   * Start heartbeat interval - sends ping every HEARTBEAT_INTERVAL_MS
   */
  private startHeartbeat(): void {
    this.stopHeartbeat(); // Clear any existing interval

    // Don't start if page is hidden
    if (!this.isPageVisible) return;

    this.heartbeatInterval = window.setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN && this.authenticated) {
        this.sendPing();
      }
    }, WS_HEARTBEAT_INTERVAL_MS);

    console.log("[RealtimeSocket] Heartbeat started");
  }

  /**
   * Stop heartbeat interval and timeout
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = undefined;
    }
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = undefined;
    }
  }

  /**
   * Send a ping message and start timeout for pong response
   */
  private sendPing(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

    const pingTimestamp = Date.now();
    this.ws.send(JSON.stringify({ type: "ping", timestamp: pingTimestamp }));

    // Set timeout for pong response
    this.heartbeatTimeout = window.setTimeout(() => {
      console.warn("[RealtimeSocket] Heartbeat timeout - no pong received, reconnecting...");
      // Force reconnect - connection may be dead
      this.handleHeartbeatTimeout();
    }, WS_HEARTBEAT_TIMEOUT_MS);
  }

  /**
   * Handle pong response - clear timeout and calculate RTT
   */
  private handlePong(timestamp: number, serverTime: number): void {
    // Clear the timeout since we got a response
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = undefined;
    }

    this.lastPongAt = Date.now();
    this.lastRtt = this.lastPongAt - timestamp;

    // Log occasionally for debugging (not every pong)
    if (Math.random() < 0.1) {
      // 10% of pongs
      console.log(
        `[RealtimeSocket] Pong received - RTT: ${this.lastRtt}ms, server time drift: ${this.lastPongAt - serverTime}ms`,
      );
    }
  }

  /**
   * Handle heartbeat timeout - connection is likely dead
   */
  private handleHeartbeatTimeout(): void {
    this.stopHeartbeat();

    // Close and reconnect
    if (this.ws) {
      this.ws.close(WS_CLOSE_HEARTBEAT_TIMEOUT, "Heartbeat timeout");
      this.ws = null;
    }

    this.authenticated = false;
    this.notifyConnectionChange(false);

    // Trigger reconnect if we have a user
    if (this.currentUserId && this.isPageVisible) {
      this.reconnectAttempts = 0; // Reset for fresh reconnect
      this.connect(this.currentUserId);
    }
  }

  /**
   * Get connection health metrics
   */
  get connectionHealth(): { lastPongAt: number; lastRtt: number; isHealthy: boolean } {
    const now = Date.now();
    const isHealthy = this.connected && now - this.lastPongAt < WS_HEARTBEAT_INTERVAL_MS + WS_HEARTBEAT_TIMEOUT_MS;
    return {
      lastPongAt: this.lastPongAt,
      lastRtt: this.lastRtt,
      isHealthy,
    };
  }

  get connected(): boolean {
    // Connection is only truly ready after first-message auth completes
    return this.ws?.readyState === WebSocket.OPEN && this.authenticated;
  }

  private async refreshSessionForReconnect(): Promise<boolean> {
    try {
      const supabase = getSupabase();
      const {
        data: { session },
      } = await supabase.auth.refreshSession();
      return Boolean(session?.access_token);
    } catch (err) {
      console.warn("[RealtimeSocket] Session refresh failed after auth error:", err);
      return false;
    }
  }

  private reconnectAfterAuthRefresh(ws: WebSocket): void {
    this.stopHeartbeat();
    clearTimeout(this.reconnectTimeout);
    this.reconnectTimeout = undefined;
    this.authenticated = false;
    this.connecting = false;
    this.pendingToken = null;
    this.authReconnectSuspended = false;
    if (this.ws === ws) {
      this.ws = null;
    }
    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
      this.suppressReconnectForWs.add(ws);
      ws.close();
    }
    window.setTimeout(() => {
      if (this.currentUserId && this.isPageVisible) {
        void this.connect(this.currentUserId);
      }
    }, 0);
  }

  private stopAfterTerminalAuthFailure(ws: WebSocket): void {
    this.stopHeartbeat();
    clearTimeout(this.reconnectTimeout);
    this.reconnectTimeout = undefined;
    this.authenticated = false;
    this.connecting = false;
    this.pendingToken = null;
    this.authReconnectSuspended = true;
    this.reconnectAttempts = 0;
    if (this.ws === ws) {
      this.ws = null;
    }
    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
      this.suppressReconnectForWs.add(ws);
      ws.close();
    }
    this.notifyConnectionChange(false);
    notifyAuthSessionExpired({ source: "realtime" });
  }

  async connect(userId: string): Promise<void> {
    // If already connected and authenticated for this user, skip
    if (this.ws?.readyState === WebSocket.OPEN && this.authenticated && this.currentUserId === userId) {
      console.log("[RealtimeSocket] Already connected for user", userId);
      return;
    }

    // If connecting, skip
    if (this.connecting) {
      console.log("[RealtimeSocket] Connection in progress, skipping");
      return;
    }

    // If connected for different user, disconnect first
    if (this.ws && this.currentUserId !== userId) {
      console.log("[RealtimeSocket] Different user, reconnecting");
      this.forceDisconnect();
    }

    this.connecting = true;
    this.authenticated = false;
    this.currentUserId = userId;
    this.authReconnectSuspended = false;

    try {
      const supabase = getSupabase();
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        console.log("[RealtimeSocket] No session token");
        this.connecting = false;
        return;
      }

      // Store token for first-message auth (not in URL for security)
      this.pendingToken = session.access_token;

      // Build WebSocket URL without token
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const wsUrl = `${protocol}//${window.location.host}/ws/realtime`;

      console.log("[RealtimeSocket] Connecting...");
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        // Send auth message immediately (first-message auth pattern)
        if (this.pendingToken) {
          console.log("[RealtimeSocket] Sending auth message...");
          ws.send(JSON.stringify({ type: "auth", token: this.pendingToken }));
          this.pendingToken = null; // Clear token after sending
        }
        // Don't notify connected yet - wait for auth_success
      };

      ws.onmessage = async (event) => {
        try {
          // Handle both text and binary messages (Bun may send either)
          let raw: string;
          if (typeof event.data === "string") {
            raw = event.data;
          } else if (event.data instanceof Blob) {
            raw = await event.data.text();
          } else if (event.data instanceof ArrayBuffer) {
            raw = new TextDecoder().decode(event.data);
          } else {
            console.warn("[RealtimeSocket] Unknown message data type, skipping");
            return;
          }

          if (!raw) {
            console.warn("[RealtimeSocket] Received empty message, skipping");
            return;
          }

          const data = JSON.parse(raw);

          // Handle auth response
          if (data.type === "auth_success") {
            this.connecting = false;
            this.authenticated = true;
            this.reconnectAttempts = 0;
            this.authRefreshAttemptedForUser = null;
            this.lastPongAt = Date.now(); // Initialize pong time
            console.log("[RealtimeSocket] Authenticated (singleton)");
            this.notifyConnectionChange(true);
            // Start heartbeat after successful auth
            this.startHeartbeat();
            return;
          }

          // Handle pong response (heartbeat)
          if (data.type === "pong") {
            this.handlePong(data.timestamp, data.serverTime);
            return;
          }

          // Handle auth error
          if (data.type === "error" && !this.authenticated) {
            console.warn("[RealtimeSocket] Auth error:", data.message);
            this.connecting = false;
            const userId = this.currentUserId;
            if (userId && this.authRefreshAttemptedForUser !== userId) {
              this.authRefreshAttemptedForUser = userId;
              const refreshed = await this.refreshSessionForReconnect();
              if (refreshed) {
                console.log("[RealtimeSocket] Session refreshed after auth error; reconnecting");
                this.reconnectAfterAuthRefresh(ws);
                return;
              }
            }
            console.warn("[RealtimeSocket] Auth failed after refresh attempt; realtime is paused until auth changes");
            this.stopAfterTerminalAuthFailure(ws);
            return;
          }

          // Cast to RealtimeEvent for type safety
          const realtimeEvent = data as RealtimeEvent;
          console.log("[RealtimeSocket] Event:", realtimeEvent.type);

          if (
            realtimeEvent.type === "new_message" ||
            realtimeEvent.type === "conversation_update" ||
            realtimeEvent.type === "message_deleted"
          ) {
            // Use Array.from() to iterate over a snapshot, preventing race conditions
            // if callbacks are added/removed during iteration
            Array.from(this.inboxCallbacks).forEach((cb) => {
              try {
                cb(realtimeEvent);
              } catch (err) {
                console.error("[RealtimeSocket] Inbox callback error:", err);
                posthog.captureException(err instanceof Error ? err : new Error(String(err)), {
                  $exception_fingerprint: "realtimeSocket:inboxCallback",
                  $exception_level: "error",
                  source: "realtimeSocket",
                  operation: "inboxCallback",
                  eventType: realtimeEvent.type,
                });
              }
            });
          }

          if (realtimeEvent.type === "calendar_event_update") {
            console.log(
              `[RealtimeSocket] Dispatching calendar_event_update to ${this.calendarCallbacks.size} callbacks`,
            );
            // Use Array.from() to iterate over a snapshot, preventing race conditions
            Array.from(this.calendarCallbacks).forEach((cb) => {
              try {
                cb(realtimeEvent);
              } catch (err) {
                console.error("[RealtimeSocket] Calendar callback error:", err);
                posthog.captureException(err instanceof Error ? err : new Error(String(err)), {
                  $exception_fingerprint: "realtimeSocket:calendarCallback",
                  $exception_level: "error",
                  source: "realtimeSocket",
                  operation: "calendarCallback",
                  eventType: realtimeEvent.type,
                });
              }
            });
          }
        } catch (err) {
          console.error("[RealtimeSocket] Parse error:", err);
          posthog.captureException(err instanceof Error ? err : new Error(String(err)), {
            $exception_fingerprint: "realtimeSocket:parseMessage",
            $exception_level: "error",
            source: "realtimeSocket",
            operation: "parseMessage",
          });
        }
      };

      ws.onclose = (event) => {
        this.connecting = false;
        this.authenticated = false;
        this.ws = null;
        this.stopHeartbeat(); // Stop heartbeat on close
        console.log(`[RealtimeSocket] Closed: code=${event.code}, wasClean=${event.wasClean}`);

        if (this.suppressReconnectForWs.has(ws)) {
          console.log("[RealtimeSocket] Close handled without auto-reconnect");
          return;
        }

        this.notifyConnectionChange(false);

        // Auto-reconnect if we still have a user (infinite retries with capped backoff)
        // This is intentional - real-time connections should persist; attempts reset on success
        // Only reconnect if page is visible to avoid wasting resources in background tabs
        if (this.currentUserId && !this.authReconnectSuspended && this.isPageVisible) {
          // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s (capped)
          const delay = Math.min(
            WS_RECONNECT_BASE_DELAY_MS * Math.pow(2, this.reconnectAttempts),
            WS_RECONNECT_MAX_DELAY_MS,
          );
          // Log warning after 10 consecutive failures to aid debugging persistent issues
          if (this.reconnectAttempts >= 10 && this.reconnectAttempts % 10 === 0) {
            console.warn(
              `[RealtimeSocket] ${this.reconnectAttempts} consecutive reconnect failures - check server health`,
            );
          }
          console.log(`[RealtimeSocket] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts + 1})`);
          this.reconnectTimeout = window.setTimeout(() => {
            this.reconnectAttempts++;
            if (this.currentUserId) {
              this.connect(this.currentUserId);
            }
          }, delay);
        } else if (this.currentUserId && this.authReconnectSuspended) {
          console.log("[RealtimeSocket] Auth reconnect paused until auth state changes");
        } else if (this.currentUserId && !this.isPageVisible) {
          console.log("[RealtimeSocket] Page hidden - will reconnect when visible");
        }
      };

      ws.onerror = (event) => {
        const eventType = typeof event?.type === "string" ? event.type : "unknown";
        console.warn(`[RealtimeSocket] Transport error event (${eventType}), readyState=${ws.readyState}`);
        this.connecting = false;
      };

      this.ws = ws;
    } catch (err) {
      console.error("[RealtimeSocket] Connection failed:", err);
      this.connecting = false;
    }
  }

  disconnect(): void {
    // Only disconnect if no subscribers
    if (this.inboxCallbacks.size > 0 || this.calendarCallbacks.size > 0) {
      console.log("[RealtimeSocket] Skipping disconnect, still have subscribers");
      return;
    }
    this.forceDisconnect();
  }

  forceDisconnect(): void {
    console.log("[RealtimeSocket] Force disconnecting...");
    clearTimeout(this.reconnectTimeout);
    this.stopHeartbeat(); // Stop heartbeat
    this.currentUserId = null; // Clear user ID to prevent auto-reconnect
    this.authenticated = false;
    this.pendingToken = null;
    this.authRefreshAttemptedForUser = null;
    this.authReconnectSuspended = false;
    this.reconnectAttempts = 0;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.notifyConnectionChange(false);
  }

  /**
   * Cleanup visibility handler (call on app unmount if needed)
   */
  destroy(): void {
    this.forceDisconnect();
    if (this.visibilityHandler && typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  subscribeToInbox(callback: RealtimeCallback): () => void {
    this.inboxCallbacks.add(callback);
    console.log(`[RealtimeSocket] Inbox subscriber added (${this.inboxCallbacks.size})`);
    return () => {
      this.inboxCallbacks.delete(callback);
      console.log(`[RealtimeSocket] Inbox subscriber removed (${this.inboxCallbacks.size})`);
    };
  }

  subscribeToCalendar(callback: RealtimeCallback): () => void {
    this.calendarCallbacks.add(callback);
    console.log(`[RealtimeSocket] Calendar subscriber added (${this.calendarCallbacks.size})`);
    return () => {
      this.calendarCallbacks.delete(callback);
      console.log(`[RealtimeSocket] Calendar subscriber removed (${this.calendarCallbacks.size})`);
    };
  }

  subscribeToConnectionChange(callback: (connected: boolean) => void): () => void {
    this.connectionChangeCallbacks.add(callback);
    // Immediately notify of current state
    callback(this.connected);
    return () => {
      this.connectionChangeCallbacks.delete(callback);
    };
  }

  subscribeToConversation(conversationId: string): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(
        JSON.stringify({
          type: "subscribe_conversation",
          conversationId,
        }),
      );
    }
  }

  /**
   * Force reconnect to pick up new connection IDs (call after OAuth)
   */
  reconnect(): void {
    const userId = this.currentUserId;
    if (userId) {
      console.log("[RealtimeSocket] Reconnecting to refresh subscriptions...");
      // Close existing connection without clearing userId
      clearTimeout(this.reconnectTimeout);
      this.authenticated = false;
      this.pendingToken = null;
      this.authReconnectSuspended = false;
      if (this.ws) {
        this.suppressReconnectForWs.add(this.ws);
        this.ws.close();
        this.ws = null;
      }
      // Reset reconnect attempts and reconnect
      this.reconnectAttempts = 0;
      this.connect(userId);
    }
  }

  private notifyConnectionChange(connected: boolean): void {
    this.connectionChangeCallbacks.forEach((cb) => {
      try {
        cb(connected);
      } catch (err) {
        console.error("[RealtimeSocket] Connection change callback error:", err);
        posthog.captureException(err instanceof Error ? err : new Error(String(err)), {
          $exception_fingerprint: "realtimeSocket:connectionChangeCallback",
          $exception_level: "warning",
          source: "realtimeSocket",
          operation: "connectionChangeCallback",
          connected,
        });
      }
    });
  }
}

// Export singleton instance
export const realtimeSocket = new RealtimeSocketManager();
